import { logUnresolvedRef } from '@/utils/blobstoreTelemetry';
import { isUrl } from './ParamsDisplayComponents';
import { findPromptInput } from './promptInput';
import { IMAGE_EXTENSIONS } from './galleryParams.consts';
import type { ResolvedAssets } from '@/types/api/assets';

export interface ImageInput {
  id: string;
  url: string;
  thumbnailUrl: string;
  order: number;
}

/** A persisted model input: the object itself when fresh from the server, its JSON after the graph round-trip. */
interface InputRef {
  url?: string;
  blobstore?: { id?: string };
  type?: string;
}

const asInputRef = (value: unknown): InputRef | undefined => {
  let parsed = value;

  if (typeof value === 'string') {
    if (!value.startsWith('{')) {
      return undefined;
    }
    try {
      parsed = JSON.parse(value);
    } catch {
      return undefined;
    }
  }

  return parsed && typeof parsed === 'object' && 'blobstore' in parsed ? (parsed as InputRef) : undefined;
};

// Cloudinary stays the fallback until it is gone, so an unresolved reference still renders.
const refUrl = (ref: InputRef, resolved: ResolvedAssets): string | undefined => {
  const assetId = ref.blobstore?.id;

  logUnresolvedRef(assetId, resolved, 'gallery_params');

  return (assetId && resolved[assetId]?.signedUrl) || ref.url;
};

const refImageUrl = (value: unknown, resolved: ResolvedAssets): string | undefined => {
  const ref = asInputRef(value);

  return ref?.type === 'image' ? refUrl(ref, resolved) : undefined;
};

export const isImageUrl = (value: string): boolean => {
  if (!isUrl(value)) return false;
  try {
    const { pathname } = new URL(value);
    const dotIndex = pathname.lastIndexOf('.');
    if (dotIndex === -1) return false;
    return IMAGE_EXTENSIONS.includes(pathname.slice(dotIndex).toLowerCase() as (typeof IMAGE_EXTENSIONS)[number]);
  } catch {
    return false;
  }
};

/**
 * Detects string URL values that represent image inputs after WASM round-trip
 * (where MediaAsset objects are serialized to plain URL strings).
 * Multi-URL values come from validateInputInfo joining arrays with '\n'.
 *
 * A URL is treated as an image if its pathname ends with a known image extension.
 */
export const extractUrlImageInputs = (
  inputs: Record<string, unknown>,
  resolved: ResolvedAssets,
): { urlImageInputs: ImageInput[]; imageUrlKeys: Set<string> } => {
  const imageUrlKeys = new Set<string>();
  const urlImageInputs: ImageInput[] = [];

  for (const [key, value] of Object.entries(inputs)) {
    const entries = typeof value === 'string' ? value.split('\n') : [value];

    const urls = entries
      .map(
        (entry) => refImageUrl(entry, resolved) ?? (typeof entry === 'string' && isImageUrl(entry) ? entry : undefined),
      )
      .filter((url): url is string => url !== undefined);
    if (urls.length === 0) continue;

    for (let i = 0; i < urls.length; i++) {
      urlImageInputs.push({ id: `${key}_${i}`, url: urls[i], thumbnailUrl: urls[i], order: i });
    }
    imageUrlKeys.add(key);
  }

  return { urlImageInputs, imageUrlKeys };
};

export const extractValue = (value: string | { value: string } | undefined): string | undefined => {
  return typeof value === 'object' && value !== null && 'value' in value ? value.value : value;
};

const extractSeed = (seed: string | { seed: string } | undefined): string | undefined => {
  return typeof seed === 'object' && seed !== null && 'seed' in seed ? seed.seed : seed;
};

export interface ProcessedInputs {
  sortedParams: Record<string, unknown>;
  prompt: string | { value: string } | undefined;
  negativePrompt: string | { value: string } | undefined;
  imageInputs: ImageInput[] | undefined;
}

// Blank an unresolvable reference rather than show its JSON: SortedParams drops empty values and lines.
const resolveMultiValueRefs = (value: string, resolved: ResolvedAssets): string => {
  return value
    .split('\n')
    .map((entry) => {
      const ref = asInputRef(entry);

      return ref ? (refUrl(ref, resolved) ?? '') : entry;
    })
    .join('\n');
};

export const processInputs = (inputs: Record<string, unknown>, resolved: ResolvedAssets): ProcessedInputs => {
  const negative = Object.entries(inputs).find(([key]) => key.includes('prompt') && key.includes('negative'))?.[1];

  const [promptKey, promptValue] = findPromptInput(inputs);

  const { urlImageInputs, imageUrlKeys } = extractUrlImageInputs(inputs, resolved);
  const imageInputs = urlImageInputs.length > 0 ? urlImageInputs : undefined;

  const remainingParams = Object.fromEntries(
    Object.entries(inputs)
      .filter(([key]) => key !== promptKey && !imageUrlKeys.has(key))
      .map(([key, value]) => {
        if (key === 'seed') {
          return [key, String(extractSeed(value as string | { seed: string } | undefined))];
        }
        // imageUrlKeys already took the image refs, so this only ever reaches non-image (e.g. video) ones.
        const ref = asInputRef(value);
        if (ref) {
          return [key, refUrl(ref, resolved) ?? ''];
        }

        return [key, typeof value === 'string' ? resolveMultiValueRefs(value, resolved) : value];
      }),
  );
  const sortedParams = Object.fromEntries(
    Object.entries(remainingParams).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
  );

  return {
    sortedParams,
    prompt: promptValue as string | { value: string } | undefined,
    negativePrompt: negative as string | { value: string } | undefined,
    imageInputs,
  };
};
